
'use client';

import { AtlyLogo } from "@/components/atoms/AtlyLogo";
import { LimitedTimeOfferCountdown } from "@/app/quiz/pre-checkout/components/LimitedTimeOfferCountdown";
import { Timer } from "@phosphor-icons/react";
import { Inter } from "next/font/google";
import classNames from "classnames";
import Image from "next/image";
import StripePaymentContainer from '@/features/stripe-precheckout-payment-elements/StripePaymentContainer';
import { StripeContextProvider } from "@/context/StripeContextProvider";
import { SubscriptionPlanOption } from "./SubscriptionPlanOption";
import { Lock } from "@phosphor-icons/react";
import FeaturesSection from "./components/FeaturesSection";
import { Testimonies } from '@/components/molecules/Testimonies';
import HeroHeading from "./components/HeroHeading";
import AsSeenOn from "./components/AsSeenOn";
import StatsSection from "./components/StatsSection";
import FAQSection from "./components/FAQSection";
import Link from "next/link";
import { useEffect, useState } from "react";
import { Plans } from "@/constants/prices";
import { motion } from "framer-motion";

const inter = Inter({ subsets: ["latin"] });

export const TestimoniesData = [
    {
        id: 2,
        text: "As someone with celiac disease, the safety rankings are super helpful. I love being able to filter for only celiac-friendly and 100% gluten-free restaurants.",
        imageSrc: "/assets/avatars/Avatar user-1.png",
        username: "Jane G.",
        glutenFreeLevel: "Highly symptomatic celiac",
        stars: 5,
    },
    {
        id: 3,
        text: "I have been using it for 3 months and all I can say is.. YESSSS! I don't feel the need to call and double check every time. So much less complicated.",
        imageSrc: "/assets/avatars/Avatar user-5.png",
        username: "Kenneth M.",
        glutenFreeLevel: "Gluten Intolerant",
        stars: 5,
    },
    {
        id: 4,
        text: "The color-coded pins make it so easy to find places. Saves me so much time. I love this map!!",
        imageSrc: "/assets/avatars/Avatar user-3.png",
        username: "Sammy F.",
        glutenFreeLevel: "Gluten Preference",
        stars: 5,
    },
    {
        id: 5,
        text: "Love how reviews from different platforms are in one place! And there is a dedicated team behind the updates, gives me peace of mind.",
        imageSrc: "/assets/avatars/Avatar user-2.png",
        username: "Derrick T.",
        glutenFreeLevel: "Symptomatic celiac",
        stars: 5,
    },
    {
        id: 6,
        text: "Knowing the safety rankings were developed with dietitians gives me peace of mind. The recommendation counts on pins super nifty too!",
        imageSrc: "/assets/avatars/Avatar user-4.png",
        username: "Shantal L.",
        glutenFreeLevel: "Symptomatic celiac",
        stars: 5,
    }
];

export const PricingPageNewDesign = ({ stripeData, testUser }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(stripeData[0]);
    const [userEmail, setUserEmail] = useState(testUser
        ? `${testUser}+${new Date().getTime()}@atly.com`
        : "");

    useEffect(() => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 300);
    }, [selectedPlan]);

    return (
        <div className="overflow-y-auto h-dvh">
            <section className={classNames("bg-gradient-porange py-6 px-5 text-white", inter.className)}>
                <div className="max-w-screen-sm mx-auto">
                    <div className="mb-9">
                        <AtlyLogo color="white" />
                        <h1 className="text-xl font-semibold mb-1 mt-4">Start your 7-day free trial</h1>
                        <div className="flex items-center gap-2 text-[15px] font-medium">
                            <Timer size={20} weight="bold" />
                            <span>Limited time offer</span>
                            <LimitedTimeOfferCountdown time={60 * 20} color="white" />
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 mb-6">
                        <SubscriptionPlanOption
                            pricePerMonth={stripeData[0].pricePerMonth}
                            priceAmount={stripeData[0].priceAmount}
                            strikedOutPrice={stripeData[1].priceAmount}
                            onPlanSelect={() => setSelectedPlan(stripeData[0])}
                            isSelected={selectedPlan.interval === Plans.YEAR}
                            plan={Plans.YEAR} />
                        <SubscriptionPlanOption
                            pricePerMonth={stripeData[1].priceAmount}
                            onPlanSelect={() => setSelectedPlan(stripeData[1])}
                            isSelected={selectedPlan.interval === Plans.MONTH}
                            plan={Plans.MONTH} />
                    </div>
                    <div className="">
                        <Image src="/assets/laurel-white.png" alt="Free trial image" width={636} height={44} />
                    </div>
                </div>
            </section>
            <section className="bg-neutral-100 pb-6">
                <div className="max-w-screen-sm mx-auto">
                    <div className="flex items-center justify-center gap-2 h-12 
                border-b border-gray-500 text-blue-950 mb-[10px]">
                        <Lock size={20} weight="bold" />
                        <span className="text-base font-semibold">Secure payment</span>
                    </div>
                    <div className="text-center my-3">
                        <h1 className="text-blue-950 text-lg font-bold">7-DAY FREE TRIAL</h1>
                        <p className="text-slate-400 text-base font-medium">No commitment. Cancel anytime.</p>
                    </div>
                    <div className="px-4 relative">
                        <motion.div
                            transition={{ duration: 0.2 }}
                            animate={{
                                opacity: isLoading ? 0 : 1,
                            }}>
                            {selectedPlan.interval === stripeData[0].interval &&
                                <StripeContextProvider {...stripeData[0]} isNewFlow>
                                    <StripePaymentContainer
                                        userEmail={userEmail}
                                        onEmailChange={setUserEmail}
                                        plan={stripeData[0].interval}
                                        variant="april" />
                                </StripeContextProvider>
                            }
                            {selectedPlan.interval === stripeData[1].interval &&
                                <StripeContextProvider {...stripeData[1]} isNewFlow>
                                    <StripePaymentContainer
                                        userEmail={userEmail}
                                        onEmailChange={setUserEmail}
                                        plan={stripeData[1].interval}
                                        variant="april" />
                                </StripeContextProvider>
                            }
                        </motion.div>
                    </div>
                    <div>
                        <div className="leading-[17.68px] text-center text-[#505a66] text-xs font-semibold mb-1">
                            You won’t be charged till {new Date(selectedPlan.trialEnd)
                                .toLocaleDateString("en-US", {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric"
                                })}<br />Cancel anytime before that date.
                        </div>
                        <div className="text-center text-[#505a66] text-[11px] font-normal tracking-wide">
                            After your free trial, the {Plans[selectedPlan.interval.toUpperCase()]} subscription is ${selectedPlan.priceAmount} and <br />
                            automatically renews every {selectedPlan.interval} until you cancel
                        </div>
                    </div>
                </div>
            </section>
            <section className="bg-white px-4 space-y-6 max-w-screen-sm mx-auto">
                <FeaturesSection variant="april" />
                <Testimonies testimonies={TestimoniesData} variant="april" />
                <HeroHeading />
                <AsSeenOn />
                <StatsSection />
                <div className="text-center py-6 text-gray-500 text-sm">
                    Cancel anytime, no questions asked
                </div>

                <FAQSection />
                <div className="mt-8 text-center text-xs text-gray-500 
                            flex justify-center space-x-2">
                    <Link
                        target='_blank'
                        href="https://www.atly.com/terms-of-use">
                        Terms of Service
                    </Link>
                    <span>•</span>
                    <Link
                        target='_blank'
                        href="https://www.atly.com/terms-of-use">
                        Privacy Policy
                    </Link>
                </div>
            </section>
        </div>
    )
}